<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  initialData: { type: Object, default: null },
});

const emit = defineEmits(['close', 'save']);

const store = useStore();

// ===== STATE =====
const messageType = ref('button');
const bodyText = ref('');
const footerText = ref('');
const escapeMessage = ref('');

// Reply buttons (max 3) – with routing + reply_message
const buttons = ref([
  { id: 'greeting-btn-1', title: '', routing: { type: '', id: null }, reply_message: '' },
]);

// List
const listButtonText = ref('Ver opções');
const sections = ref([
  {
    title: '',
    rows: [
      { id: 'row-1', title: '', description: '', routing: { type: '', id: null }, reply_message: '' },
    ],
  },
]);

// ===== AGENTS & TEAMS =====
const agents = computed(() => store.getters['agents/getAgents'] || []);
const teams = computed(() => store.getters['teams/getTeams'] || []);

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
});

// ===== SYNC INITIAL DATA =====
const isSyncing = ref(false);

watch(
  () => props.show,
  val => {
    if (val && props.initialData) {
      syncFromData(props.initialData);
    } else if (val && !props.initialData) {
      resetForm();
    }
  }
);

function syncFromData(data) {
  isSyncing.value = true;
  messageType.value = data.type || 'button';
  bodyText.value = data.body?.text || '';
  footerText.value = data.footer?.text || '';
  escapeMessage.value = data.escape_message || '';

  if (data.type === 'button') {
    const btns = (data.action?.buttons || []).map((b, i) => ({
      id: b.reply?.id || `greeting-btn-${i + 1}`,
      title: b.reply?.title || '',
      routing: b.reply?.routing || { type: '', id: null },
      reply_message: b.reply?.reply_message || '',
    }));
    buttons.value = btns.length ? btns : [{ id: 'greeting-btn-1', title: '', routing: { type: '', id: null }, reply_message: '' }];
  } else if (data.type === 'list') {
    listButtonText.value = data.action?.button || 'Ver opções';
    const secs = (data.action?.sections || []).map((s, si) => ({
      title: s.title || '',
      rows: (s.rows || []).map((r, ri) => ({
        id: r.id || `s${si}-row-${ri + 1}`,
        title: r.title || '',
        description: r.description || '',
        routing: r.routing || { type: '', id: null },
        reply_message: r.reply_message || '',
      })),
    }));
    sections.value = secs.length
      ? secs
      : [{ title: '', rows: [{ id: 'row-1', title: '', description: '', routing: { type: '', id: null }, reply_message: '' }] }];
  }
  isSyncing.value = false;
}

function resetForm() {
  messageType.value = 'button';
  bodyText.value = '';
  footerText.value = '';
  escapeMessage.value = '';
  buttons.value = [{ id: 'greeting-btn-1', title: '', routing: { type: '', id: null }, reply_message: '' }];
  listButtonText.value = 'Ver opções';
  sections.value = [
    { title: '', rows: [{ id: 'row-1', title: '', description: '', routing: { type: '', id: null }, reply_message: '' }] },
  ];
}

// Reset lists on type change (only when user manually switches, not during sync)
// flush: 'sync' ensures the callback runs immediately when messageType changes,
// so isSyncing is still true during syncFromData and the reset is skipped.
watch(messageType, () => {
  if (isSyncing.value) return;
  buttons.value = [{ id: 'greeting-btn-1', title: '', routing: { type: '', id: null }, reply_message: '' }];
  sections.value = [
    { title: '', rows: [{ id: 'row-1', title: '', description: '', routing: { type: '', id: null }, reply_message: '' }] },
  ];
}, { flush: 'sync' });

// ===== BUTTONS =====
function addButton() {
  if (buttons.value.length >= 3) return;
  buttons.value.push({
    id: `greeting-btn-${buttons.value.length + 1}`,
    title: '',
    routing: { type: '', id: null },
    reply_message: '',
  });
}
function removeButton(idx) {
  if (buttons.value.length <= 1) return;
  buttons.value.splice(idx, 1);
}

// ===== SECTIONS =====
function addSection() {
  sections.value.push({
    title: '',
    rows: [{ id: `row-1`, title: '', description: '', routing: { type: '', id: null } }],
  });
}
function removeSection(sIdx) {
  if (sections.value.length <= 1) return;
  sections.value.splice(sIdx, 1);
}

// ===== ROWS =====
function addRow(sIdx) {
  const totalRows = sections.value.reduce((sum, s) => sum + s.rows.length, 0);
  if (totalRows >= 20) return;
  const section = sections.value[sIdx];
  section.rows.push({
    id: `row-${section.rows.length + 1}`,
    title: '',
    description: '',
    routing: { type: '', id: null },
    reply_message: '',
  });
}
function removeRow(sIdx, rIdx) {
  const section = sections.value[sIdx];
  if (section.rows.length <= 1 && sections.value.length <= 1) return;
  section.rows.splice(rIdx, 1);
  if (section.rows.length === 0) {
    sections.value.splice(sIdx, 1);
  }
}

function moveRowUp(sIdx, rIdx) {
  if (rIdx <= 0) return;
  const rows = sections.value[sIdx].rows;
  [rows[rIdx - 1], rows[rIdx]] = [rows[rIdx], rows[rIdx - 1]];
}

function moveRowDown(sIdx, rIdx) {
  const rows = sections.value[sIdx].rows;
  if (rIdx >= rows.length - 1) return;
  [rows[rIdx], rows[rIdx + 1]] = [rows[rIdx + 1], rows[rIdx]];
}

// ===== VALIDATION =====
const isValid = computed(() => {
  if (!bodyText.value.trim()) return false;
  if (bodyText.value.length > 1024) return false;
  if (footerText.value.length > 60) return false;

  if (messageType.value === 'button') {
    return (
      buttons.value.length >= 1 &&
      buttons.value.every(b => b.title.trim() && b.title.length <= 20)
    );
  }

  // list
  if (!listButtonText.value.trim() || listButtonText.value.length > 20) return false;
  return sections.value.every(
    s =>
      s.rows.length >= 1 &&
      s.rows.every(
        r => r.title.trim() && r.title.length <= 24 && r.description.length <= 72
      )
  );
});

// ===== PAYLOAD BUILD =====
function buildPayload() {
  if (messageType.value === 'button') {
    return {
      type: 'button',
      body: { text: bodyText.value.trim() },
      ...(footerText.value.trim()
        ? { footer: { text: footerText.value.trim() } }
        : {}),
      action: {
        buttons: buttons.value
          .filter(b => b.title.trim())
          .map((b, i) => ({
            type: 'reply',
            reply: {
              id: b.id || `greeting-btn-${i + 1}`,
              title: b.title.trim(),
              routing: b.routing?.type
                ? { type: b.routing.type, id: Number(b.routing.id) }
                : undefined,
              reply_message: b.reply_message?.trim() || undefined,
            },
          })),
      },
      ...(escapeMessage.value.trim()
        ? { escape_message: escapeMessage.value.trim() }
        : {}),
    };
  }

  // list
  return {
    type: 'list',
    body: { text: bodyText.value.trim() },
    ...(footerText.value.trim()
      ? { footer: { text: footerText.value.trim() } }
      : {}),
    action: {
      button: listButtonText.value.trim() || 'Ver opções',
      sections: sections.value.map(s => ({
        title: s.title.trim(),
        rows: s.rows
          .filter(r => r.title.trim())
          .map((r, i) => ({
            id: r.id || `row-${i + 1}`,
            title: r.title.trim(),
            description: r.description?.trim() || undefined,
            routing: r.routing?.type
              ? { type: r.routing.type, id: Number(r.routing.id) }
              : undefined,
            reply_message: r.reply_message?.trim() || undefined,
          })),
      })),
    },
    ...(escapeMessage.value.trim()
      ? { escape_message: escapeMessage.value.trim() }
      : {}),
  };
}

function onSave() {
  if (!isValid.value) return;
  emit('save', buildPayload());
}

function onClose() {
  emit('close');
}

// ===== PREVIEW HELPERS =====
const previewButtons = computed(() =>
  buttons.value.filter(b => b.title.trim()).map(b => ({
    title: b.title.trim(),
    routing: b.routing,
  }))
);
const previewSections = computed(() =>
  sections.value
    .map(s => ({
      title: s.title.trim(),
      rows: s.rows.filter(r => r.title.trim()),
    }))
    .filter(s => s.rows.length > 0)
);

const selectChevronStyle = computed(() => ({
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
}));
</script>

<template>
  <woot-modal :show="show" @close="onClose" class="greeting-interactive-modal">
    <div class="flex flex-col h-full max-h-[85vh]">
      <div class="flex items-center px-6 py-4 border-b border-n-weak">
        <div class="flex items-center gap-2">
          <span class="i-lucide-route size-5 text-green-600" />
          <h2 class="text-base font-semibold text-n-slate-12">
            URA Interativa — Saudação
          </h2>
        </div>
      </div>

      <div
        class="flex items-start gap-3 mx-6 mt-4 p-3.5 rounded-xl bg-n-blue-2 ring-1 ring-n-blue-6"
      >
        <span
          class="i-lucide-lightbulb size-4 text-n-blue-11 mt-0.5 flex-shrink-0"
        />
        <p class="text-xs leading-relaxed text-n-blue-11">
          Configure uma URA inicial para rotear conversas automaticamente. O
          contato receberá uma
          <strong>mensagem interativa do WhatsApp</strong> e, ao selecionar uma
          opção, a conversa será atribuída ao agente ou time configurado.
        </p>
      </div>

      <div class="flex flex-1 overflow-hidden">
        <div class="flex-1 overflow-y-auto p-6 space-y-4">
          <div class="flex gap-2">
            <button
              class="flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all cursor-pointer"
              :class="
                messageType === 'button'
                  ? 'bg-green-600 text-white'
                  : 'bg-n-background text-n-slate-11 border border-n-weak'
              "
              @click="messageType = 'button'"
            >
              <span
                class="i-lucide-mouse-pointer-click size-3.5 inline-block align-middle mr-1"
              />
              Botões (máx. 3)
            </button>
            <button
              class="flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all cursor-pointer"
              :class="
                messageType === 'list'
                  ? 'bg-green-600 text-white'
                  : 'bg-n-background text-n-slate-11 border border-n-weak'
              "
              @click="messageType = 'list'"
            >
              <span
                class="i-lucide-list size-3.5 inline-block align-middle mr-1"
              />
              Lista (máx. 20)
            </button>
          </div>

          <div>
            <label class="text-xs font-medium text-n-slate-10 mb-1 block">
              Corpo da mensagem *
              <span class="text-n-slate-9">(max 1024)</span>
            </label>
            <textarea
              v-model="bodyText"
              class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 outline-none focus:border-green-500 resize-none"
              rows="3"
              placeholder="Olá! 👋 Bem-vindo ao nosso atendimento. Como podemos te ajudar?"
              maxlength="1024"
            />
            <span
              v-if="bodyText.length > 900"
              class="text-[10px] text-amber-600"
            >
              {{ 1024 - bodyText.length }} restantes
            </span>
          </div>

          <div>
            <label class="text-xs font-medium text-n-slate-10 mb-1 block">
              Rodapé
              <span class="text-n-slate-9">(opcional, max 60)</span>
            </label>
            <input
              v-model="footerText"
              class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 outline-none focus:border-green-500"
              placeholder="Selecione uma opção abaixo"
              maxlength="60"
            />
          </div>

          <div
            v-if="messageType === 'button'"
            class="border border-n-weak rounded-lg p-4"
          >
            <div class="flex items-center justify-between mb-3">
              <p
                class="text-xs font-bold text-n-slate-12 uppercase tracking-wider flex items-center gap-1.5"
              >
                <span class="i-lucide-mouse-pointer-click size-3.5 text-green-600" />
                Botões de Resposta
              </p>
              <button
                v-if="buttons.length < 3"
                class="text-[11px] font-medium cursor-pointer text-green-600 hover:text-green-700"
                @click="addButton"
              >
                + Adicionar botão
              </button>
            </div>
            <p class="text-[10px] text-n-slate-10 mb-3">
              Mínimo 1, máximo 3 botões. Cada título até 20 caracteres.
            </p>

            <div class="flex flex-col gap-2.5">
              <div
                v-for="(btn, idx) in buttons"
                :key="idx"
                class="rounded-lg border border-n-weak"
              >
                <div class="flex items-center gap-2 px-3 py-2.5">
                  <span
                    class="flex items-center justify-center size-5 rounded-full bg-green-600 text-white text-[10px] font-bold flex-shrink-0"
                  >
                    {{ idx + 1 }}
                  </span>
                  <input
                    v-model="btn.title"
                    class="flex-1 text-sm bg-transparent text-n-slate-12 focus:outline-none placeholder:text-n-slate-9"
                    :placeholder="`Título do botão ${idx + 1}`"
                    maxlength="20"
                  />
                  <span
                    class="text-[10px] tabular-nums text-n-slate-9 flex-shrink-0"
                  >
                    {{ btn.title.length }}/20
                  </span>
                  <button
                    v-if="buttons.length > 1"
                    class="text-n-slate-10 hover:text-red-500 cursor-pointer transition-colors flex-shrink-0"
                    @click="removeButton(idx)"
                  >
                    <span class="i-lucide-trash-2 size-3.5" />
                  </button>
                </div>
                <div class="px-3 py-2.5 border-t border-n-weak">
                  <div class="flex items-center gap-2 mb-1.5">
                    <span
                      class="i-lucide-arrow-right size-3 text-n-slate-9 flex-shrink-0"
                    />
                    <span class="text-[11px] text-n-slate-10">
                      Direcionar para:
                    </span>
                    <select
                      v-model="btn.routing.type"
                      :style="selectChevronStyle"
                      class="appearance-none px-2 py-1 pr-6 rounded-lg text-xs bg-transparent text-n-slate-12 ring-1 ring-n-weak focus:ring-green-500 focus:outline-none cursor-pointer bg-no-repeat bg-[length:12px] bg-[position:right_6px_center]"
                      @change="btn.routing.id = null"
                    >
                      <option value="">Nenhum</option>
                      <option value="agent">Agente</option>
                      <option value="team">Time</option>
                    </select>
                  </div>
                  <div v-if="btn.routing.type" class="ml-6">
                    <select
                      v-if="btn.routing.type === 'agent'"
                      v-model="btn.routing.id"
                      :style="selectChevronStyle"
                      class="appearance-none w-full px-2.5 py-1.5 pr-7 rounded-lg text-xs bg-transparent text-n-slate-12 ring-1 ring-n-weak focus:ring-green-500 focus:outline-none cursor-pointer bg-no-repeat bg-[length:12px] bg-[position:right_8px_center]"
                    >
                      <option :value="null">Selecione um agente...</option>
                      <option
                        v-for="a in agents"
                        :key="a.id"
                        :value="a.id"
                      >
                        {{ a.name }}
                      </option>
                    </select>
                    <select
                      v-if="btn.routing.type === 'team'"
                      v-model="btn.routing.id"
                      :style="selectChevronStyle"
                      class="appearance-none w-full px-2.5 py-1.5 pr-7 rounded-lg text-xs bg-transparent text-n-slate-12 ring-1 ring-n-weak focus:ring-green-500 focus:outline-none cursor-pointer bg-no-repeat bg-[length:12px] bg-[position:right_8px_center]"
                    >
                      <option :value="null">Selecione um time...</option>
                      <option
                        v-for="t in teams"
                        :key="t.id"
                        :value="t.id"
                      >
                        {{ t.name }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="px-3 py-2.5 border-t border-n-weak">
                  <div class="flex items-center gap-1.5 mb-1.5">
                    <span class="i-lucide-message-square-reply size-3 text-n-slate-9 flex-shrink-0" />
                    <span class="text-[11px] text-n-slate-10">Mensagem de retorno (opcional)</span>
                  </div>
                  <input
                    v-model="btn.reply_message"
                    class="w-full px-2.5 py-1.5 rounded-lg text-xs bg-transparent text-n-slate-12 ring-1 ring-n-weak focus:ring-green-500 focus:outline-none"
                    placeholder="Ex: Estou te direcionando ao setor..."
                    maxlength="256"
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="messageType === 'list'"
            class="border border-n-weak rounded-lg p-4"
          >
            <div class="flex items-center justify-between mb-3">
              <p
                class="text-xs font-bold text-n-slate-12 uppercase tracking-wider flex items-center gap-1.5"
              >
                <span class="i-lucide-list size-3.5 text-green-600" />
                Configuração da Lista
              </p>
            </div>

            <div class="mb-4">
              <label class="text-xs text-n-slate-10 mb-1 block">
                Texto do botão * (max 20)
              </label>
              <input
                v-model="listButtonText"
                class="w-full px-3 py-1.5 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 outline-none focus:border-green-500"
                placeholder="Ver opções"
                maxlength="20"
              />
            </div>

            <div
              v-for="(section, sIdx) in sections"
              :key="sIdx"
              class="mb-3 border border-n-weak rounded-lg p-3"
            >
              <div class="flex items-center justify-between mb-2">
                <label class="text-xs font-medium text-n-slate-10">
                  Seção {{ sIdx + 1 }}
                </label>
                <button
                  v-if="sections.length > 1"
                  class="text-[10px] text-red-500 hover:text-red-600 cursor-pointer"
                  @click="removeSection(sIdx)"
                >
                  Remover seção
                </button>
              </div>
              <input
                v-model="section.title"
                class="w-full px-3 py-1.5 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 outline-none focus:border-green-500 mb-2"
                placeholder="Título da seção (opcional)"
                maxlength="24"
              />

              <div
                v-for="(row, rIdx) in section.rows"
                :key="rIdx"
                class="mb-2 border border-n-weak/50 rounded-lg p-2.5"
              >
                <div class="flex gap-2 items-start">
                  <div class="flex-1 space-y-1">
                    <input
                      v-model="row.title"
                      class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-12 outline-none focus:border-green-500"
                      :placeholder="`Título do item ${rIdx + 1} (max 24)`"
                      maxlength="24"
                    />
                    <input
                      v-model="row.description"
                      class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-10 outline-none focus:border-green-500"
                      :placeholder="`Descrição (opcional, max 72)`"
                      maxlength="72"
                    />
                  </div>
                  <button
                    v-if="rIdx > 0"
                    class="text-n-slate-10 hover:text-green-600 cursor-pointer transition-colors"
                    title="Mover para cima"
                    @click="moveRowUp(sIdx, rIdx)"
                  >
                    <span class="i-lucide-arrow-up size-3.5" />
                  </button>
                  <button
                    v-if="rIdx < section.rows.length - 1"
                    class="text-n-slate-10 hover:text-green-600 cursor-pointer transition-colors"
                    title="Mover para baixo"
                    @click="moveRowDown(sIdx, rIdx)"
                  >
                    <span class="i-lucide-arrow-down size-3.5" />
                  </button>
                  <button
                    v-if="section.rows.length > 1"
                    class="text-n-slate-10 hover:text-red-500 cursor-pointer mt-1"
                    @click="removeRow(sIdx, rIdx)"
                  >
                    <span class="i-lucide-x size-3.5" />
                  </button>
                </div>
                <div class="mt-2 pt-2 border-t border-n-weak/50">
                  <div class="flex items-center gap-2 mb-1.5">
                    <span
                      class="i-lucide-arrow-right size-2.5 text-n-slate-9 flex-shrink-0"
                    />
                    <span class="text-[11px] text-n-slate-10">
                      Direcionar:
                    </span>
                    <select
                      v-model="row.routing.type"
                      :style="selectChevronStyle"
                      class="appearance-none px-2 py-0.5 pr-5 rounded-md text-[11px] bg-transparent text-n-slate-12 ring-1 ring-n-weak focus:ring-green-500 focus:outline-none cursor-pointer bg-no-repeat bg-[length:10px] bg-[position:right_4px_center]"
                      @change="row.routing.id = null"
                    >
                      <option value="">Nenhum</option>
                      <option value="agent">Agente</option>
                      <option value="team">Time</option>
                    </select>
                  </div>
                  <div v-if="row.routing.type" class="ml-5">
                    <select
                      v-if="row.routing.type === 'agent'"
                      v-model="row.routing.id"
                      :style="selectChevronStyle"
                      class="appearance-none w-full px-2.5 py-1 pr-7 rounded-md text-[11px] bg-transparent text-n-slate-12 ring-1 ring-n-weak focus:ring-green-500 focus:outline-none cursor-pointer bg-no-repeat bg-[length:12px] bg-[position:right_8px_center]"
                    >
                      <option :value="null">Selecione um agente...</option>
                      <option
                        v-for="a in agents"
                        :key="a.id"
                        :value="a.id"
                      >
                        {{ a.name }}
                      </option>
                    </select>
                    <select
                      v-if="row.routing.type === 'team'"
                      v-model="row.routing.id"
                      :style="selectChevronStyle"
                      class="appearance-none w-full px-2.5 py-1 pr-7 rounded-md text-[11px] bg-transparent text-n-slate-12 ring-1 ring-n-weak focus:ring-green-500 focus:outline-none cursor-pointer bg-no-repeat bg-[length:12px] bg-[position:right_8px_center]"
                    >
                      <option :value="null">Selecione um time...</option>
                      <option
                        v-for="t in teams"
                        :key="t.id"
                        :value="t.id"
                      >
                        {{ t.name }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="mt-2 pt-2 border-t border-n-weak/50">
                  <div class="flex items-center gap-1.5 mb-1">
                    <span class="i-lucide-message-square-reply size-2.5 text-n-slate-9 flex-shrink-0" />
                    <span class="text-[10px] text-n-slate-10">Mensagem de retorno (opcional)</span>
                  </div>
                  <input
                    v-model="row.reply_message"
                    class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-12 outline-none focus:border-green-500"
                    placeholder="Ex: Encaminhando para o setor..."
                    maxlength="256"
                  />
                </div>
              </div>
              <button
                class="text-[10px] font-medium cursor-pointer text-green-600 hover:text-green-700 flex items-center gap-1"
                @click="addRow(sIdx)"
              >
                <span class="i-lucide-plus size-3" />
                Adicionar item
              </button>
            </div>
            <button
              v-if="sections.length < 10"
              class="text-xs font-medium cursor-pointer text-green-600 hover:text-green-700 flex items-center gap-1"
              @click="addSection"
            >
              <span class="i-lucide-plus size-3.5" />
              Adicionar seção
            </button>
          </div>

          <div class="border-t border-n-weak pt-4">
            <label
              class="text-xs font-medium text-n-slate-12 mb-1.5 flex items-center gap-1.5"
            >
              <span class="i-lucide-shield-alert size-3.5 text-amber-500" />
              Mensagem de escape
            </label>
            <p class="text-[10px] text-n-slate-10 mb-2">
              Enviada quando o cliente responde algo que não corresponde a
              nenhuma opção. O menu será reenviado automaticamente.
            </p>
            <textarea
              v-model="escapeMessage"
              rows="2"
              class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 outline-none focus:border-green-500 resize-none"
              placeholder="Desculpe, não entendi. Por favor, selecione uma das opções abaixo:"
              maxlength="256"
            />
          </div>
        </div>

        <div
          class="w-80 border-l border-n-weak bg-n-background p-4 overflow-y-auto flex-shrink-0"
        >
          <p
            class="text-xs font-bold text-n-slate-10 uppercase tracking-wider mb-3 flex items-center gap-1.5"
          >
            <span class="i-lucide-eye size-3.5" />
            Preview
          </p>

          <div
            class="rounded-2xl overflow-hidden"
            style="background: #e5ddd5"
          >
            <div
              class="flex items-center justify-between px-3 py-1.5"
              style="background: #075e54"
            >
              <div class="flex items-center gap-1.5">
                <span class="i-lucide-chevron-left size-3.5 text-white/80" />
                <div
                  class="size-6 rounded-full bg-white/20 flex items-center justify-center"
                >
                  <span class="i-lucide-user size-3.5 text-white/60" />
                </div>
                <span class="text-[11px] font-medium text-white">Contato</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="i-lucide-phone size-3 text-white/70" />
                <span class="i-lucide-more-vertical size-3 text-white/70" />
              </div>
            </div>
            <div class="p-3 min-h-[200px]">
              <div
                class="rounded-lg p-3 max-w-[260px] shadow-sm"
                style="background: #dcf8c6"
              >
                <p
                  class="text-xs leading-relaxed whitespace-pre-wrap"
                  style="color: #303030"
                >
                  {{ bodyText || 'Corpo da mensagem...' }}
                </p>
                <p
                  v-if="footerText.trim()"
                  class="text-[10px] mt-2"
                  style="color: #8696a0"
                >
                  {{ footerText }}
                </p>
                <span
                  class="text-[9px] float-right mt-1 ml-2"
                  style="color: #8696a0"
                >
                  12:00 ✓✓
                </span>
                <div class="clear-both" />
              </div>

              <div
                v-if="messageType === 'button' && previewButtons.length > 0"
                class="mt-1 max-w-[260px]"
              >
                <div
                  v-for="(btn, idx) in previewButtons"
                  :key="idx"
                  class="rounded-lg py-2 px-3 text-center text-xs font-medium mt-1 shadow-sm"
                  style="background: #fff; color: #00a884"
                >
                  {{ btn.title }}
                  <span
                    v-if="btn.routing?.type"
                    class="block text-[9px] mt-0.5 font-normal"
                    style="color: #8696a0"
                  >
                    →
                    {{ btn.routing.type === 'agent' ? 'Agente' : 'Time' }}
                  </span>
                </div>
              </div>

              <div
                v-if="messageType === 'list' && listButtonText.trim()"
                class="mt-1 max-w-[260px]"
              >
                <div
                  class="rounded-lg py-2 px-3 text-center text-xs font-medium shadow-sm flex items-center justify-center gap-1"
                  style="background: #fff; color: #00a884"
                >
                  <span class="i-lucide-list size-3.5" />
                  {{ listButtonText }}
                </div>
                <div
                  v-if="previewSections.length > 0"
                  class="mt-2 rounded-lg overflow-hidden shadow-sm"
                  style="background: #fff"
                >
                  <div
                    v-for="(sec, sIdx) in previewSections"
                    :key="sIdx"
                  >
                    <div
                      v-if="sec.title"
                      class="px-3 py-1.5 text-[10px] font-bold uppercase"
                      style="color: #00a884; background: #f0f2f5"
                    >
                      {{ sec.title }}
                    </div>
                    <div
                      v-for="(row, rIdx) in sec.rows"
                      :key="rIdx"
                      class="px-3 py-2 border-b last:border-b-0"
                      style="border-color: #f0f2f5"
                    >
                      <p class="text-xs font-medium" style="color: #303030">
                        {{ row.title }}
                      </p>
                      <p
                        v-if="row.description"
                        class="text-[10px]"
                        style="color: #8696a0"
                      >
                        {{ row.description }}
                      </p>
                      <p
                        v-if="row.routing?.type"
                        class="text-[9px] mt-0.5"
                        style="color: #8696a0"
                      >
                        →
                        {{
                          row.routing.type === 'agent'
                            ? 'Agente'
                            : 'Time'
                        }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p class="text-[10px] text-n-slate-10 mt-2 text-center">
            Visualização aproximada de como a mensagem aparecerá no WhatsApp
          </p>
        </div>
      </div>

      <div
        class="flex items-center justify-end gap-2 px-6 py-4 border-t border-n-weak"
      >
        <NextButton label="Cancelar" slate faded sm @click="onClose" />
        <NextButton
          label="Salvar URA"
          teal
          sm
          :disabled="!isValid"
          @click="onSave"
        />
      </div>
    </div>
  </woot-modal>
</template>

<style scoped>
.greeting-interactive-modal :deep(.modal-container) {
  max-width: 850px;
  width: 95vw;
}
</style>
